import { useState, useCallback } from "react";
import { useDemoMode } from "@/pages/demo/DemoModeContext";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { DocumentType, DOCUMENT_TYPE_OPTIONS } from "@/shared/types/documentTypes";

// Local type for pending document uploads (before they're saved to DB)
interface PendingDocument {
  file: File;
  name: string;
  documentType: DocumentType;
}
import { useSnackbar } from "notistack";
import { uploadDocument, DocumentLimitError } from "@/shared/utils/uploadDocument";
import DocumentUploadInput from "@/shared/components/document-viewer/DocumentUploadInput";
import { UploadedDocument } from "@/shared/components/document-viewer/UploadedDocument";
import Loader from "@/shared/components/Loader";
import { sentryUtils } from "@/shared/utils/sentry";

interface LawyerUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  estateId: number;
}

export default function LawyerUploadDialog({
  isOpen,
  onClose,
  onComplete,
  estateId,
}: LawyerUploadDialogProps) {
  const [documents, setDocuments] = useState<PendingDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [errorIndexes, setErrorIndexes] = useState<number[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const { isDemoMode } = useDemoMode();

  const handleFileAdd = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newDocuments: PendingDocument[] = Array.from(files).map((file) => ({
      file,
      name: file.name,
      documentType: DocumentType.OTHER,
    }));

    setDocuments((prev) => [...prev, ...newDocuments]);

    // Clear file input
    event.target.value = "";
  }, []);

  const handleDocumentTypeChange = useCallback((type: DocumentType, index: number) => {
    setDocuments((prev) => {
      const updated = [...prev];
      updated[index].documentType = type;
      return updated;
    });
    // Clear error for this document
    setErrorIndexes((prev) => prev.filter((i) => i !== index));
  }, []);

  const handleDocumentRemove = useCallback((index: number) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
    setErrorIndexes((prev) => prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i)));
  }, []);

  const validateDocuments = (): boolean => {
    const errors: number[] = [];
    documents.forEach((doc, index) => {
      if (!doc.documentType) {
        errors.push(index);
      }
    });
    setErrorIndexes(errors);
    return errors.length === 0;
  };

  const handleUpload = async () => {
    if (isDemoMode) {
      onClose();
      return;
    }
    if (documents.length === 0) {
      enqueueSnackbar("Please add at least 1 document", { variant: "warning" });
      return;
    }

    if (!validateDocuments()) {
      enqueueSnackbar("Please select a document type for all documents.", { variant: "warning" });
      return;
    }

    setUploading(true);

    try {
      for (const doc of documents) {
        if (doc.file) {
          await uploadDocument(doc.file, doc.documentType, estateId);
        }
      }

      enqueueSnackbar(
        `${documents.length} ${documents.length === 1 ? "document" : "documents"} uploaded.`,
        { variant: "success" },
      );
      setDocuments([]);
      setErrorIndexes([]);
      onComplete();
    } catch (error: unknown) {
      enqueueSnackbar("Could not upload documents. Please try again.", { variant: "error" });
      if (!(error instanceof DocumentLimitError)) {
        console.error("Upload error:", error);
        sentryUtils.captureError(error, "Document upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  const handleClose = (open: boolean) => {
    if (!uploading && !open) {
      setDocuments([]);
      setErrorIndexes([]);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="md:w-[60%] md:max-h-[90%] max-w-none"
        aria-describedby="lawyer-upload-dialog-description"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Upload Document to Client</DialogTitle>
        </DialogHeader>

        <DialogBody className="flex-1 min-h-0 overflow-y-auto px-4 py-6 md:px-[32px] md:py-[24px]">
          {uploading ? (
            <div className="flex items-center justify-center min-h-[250px]">
              <Loader
                size="lg"
                label={`Uploading ${documents.length > 1 ? "documents" : "document"}...`}
              />
            </div>
          ) : (
            <div className="gap-6 flex flex-col w-full">
              <DocumentUploadInput
                onChange={handleFileAdd}
                multiple={true}
                disabled={false}
                className="w-full"
              />
              {documents.length > 0 && (
                <div className="flex flex-col flex-grow max-h-[500px] gap-4 overflow-auto">
                  {documents.map((document, ix) => (
                    <UploadedDocument
                      key={`${document.name}-${ix}`}
                      documentTypes={DOCUMENT_TYPE_OPTIONS}
                      document={document}
                      documentType={document.documentType}
                      ix={ix}
                      onDocumentTypeChange={(v: DocumentType) => {
                        handleDocumentTypeChange(v, ix);
                      }}
                      onDocumentRemove={() => {
                        handleDocumentRemove(ix);
                      }}
                      isErrored={errorIndexes.includes(ix)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogBody>

        <div className="flex flex-row justify-between flex-shrink-0">
          <Button onClick={() => handleClose(false)} variant="ghost" disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={uploading || documents.length === 0}
            className={uploading ? "opacity-50 cursor-not-allowed" : ""}
          >
            {uploading ? "Uploading..." : "Finish"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
